import { useState } from "react";
import { useFormContext } from "react-hook-form";

import ProfessionFields from "./fields/ProfessionFields";
import { isProfessionComplete } from "./fields/preferencesValidation";
import OnboardingWizardStepFooter from "./OnboardingWizardStepFooter";
import OnboardingWizardStepHeader from "./OnboardingWizardStepHeader";
import type { WizardFormValues } from "./onboardingWizardForm";
import useOnboardingTracking from "./useOnboardingTracking";
import { useWizardMode } from "./WizardModeContext";
import { useOnboarding, useProfessions } from "~/hooks";

const OnboardingWizardStepProfession = () => {
	const { professions } = useProfessions();
	const { mode } = useWizardMode();
	const { track } = useOnboardingTracking();
	const { handleUpdate, setStep } = useOnboarding();
	const {
		watch,
		trigger,
		getValues,
		formState: { isSubmitting },
	} = useFormContext<WizardFormValues>();

	const primaryProfessionId = watch("primaryProfessionId");
	const secondaryProfessionId = watch("secondaryProfessionId");
	const cataMemberNumber = watch("cataMemberNumber");

	const [isDisclaimerAccepted, setIsDisclaimerAccepted] = useState(false);

	const canSubmit = isProfessionComplete(
		{ primaryProfessionId, secondaryProfessionId, cataMemberNumber },
		professions,
		isDisclaimerAccepted,
	);

	const handleNext = async () => {
		if (!(await trigger("primaryProfessionId"))) return;
		const v = getValues();
		if (mode === "live") {
			await handleUpdate({
				primaryProfessionId: v.primaryProfessionId,
				secondaryProfessionId: v.secondaryProfessionId || null,
				cataMemberNumber: v.cataMemberNumber || null,
			});
		}
		track("onboarding:step_completed", {
			primary_profession_id: v.primaryProfessionId,
			has_secondary: !!v.secondaryProfessionId,
			has_cata_number: !!v.cataMemberNumber,
		});
		setStep("certification");
	};

	return (
		<div className="flex h-full min-h-0 flex-1 flex-col gap-4 phablet:gap-6">
			<OnboardingWizardStepHeader
				title="What's your profession?"
				subtitle="We use this to apply the right accreditations to your certificates."
			/>

			<div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
				<ProfessionFields
					isDisclaimerAccepted={isDisclaimerAccepted}
					setIsDisclaimerAccepted={setIsDisclaimerAccepted}
				/>
			</div>

			<OnboardingWizardStepFooter
				primary={{ onClick: handleNext, disabled: !canSubmit || isSubmitting }}
			/>
		</div>
	);
};

export default OnboardingWizardStepProfession;
